import { mat4, ReadonlyMat4, quat, ReadonlyVec3, vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';

/**
 * Snap a world-space position onto the image grid's voxel lattice.
 *
 * Projects the position into the image grid's voxel coordinate frame,
 * rounds each axis to the nearest integer voxel boundary, and converts
 * back to world space. This guarantees that any Grid constructed with
 * the snapped position shares the same voxel phase as the image grid,
 * which prevents sub-voxel misalignment in downstream operations
 * (boolean ops, margin expansion, dose mapping).
 *
 * @param point      Position in patient (world) coordinates
 * @param imageGrid  Reference image grid that defines the canonical lattice
 * @returns          Snapped position in patient (world) coordinates
 */
export function snapPointToImageGrid(
    point: ReadonlyVec3,
    imageGrid: Grid
): vec3 {
    const voxel = imageGrid.toVoxelSpace(point);

    // Snap each axis to nearest voxel boundary
    voxel[0] = Math.round(voxel[0]);
    voxel[1] = Math.round(voxel[1]);
    voxel[2] = Math.round(voxel[2]);

    return imageGrid.toWorldSpace(voxel);
}

/**
 * Build a copy of a grid whose origin has been snapped to the image
 * grid's voxel lattice. Dimensions and resolution are preserved;
 * only the origin changes.
 *
 * Used on mouse release in transform actions so the committed
 * structure stays aligned with the canonical image grid.
 */
export function buildSnappedGrid(grid: Grid, imageGrid: Grid): Grid {
    const snappedOrigin = snapPointToImageGrid(grid.getPosition(), imageGrid);
    const orientation = grid.getOrientation();
    const snappedTransform = mat4.fromRotationTranslation(
        mat4.create(),
        quat.fromMat3(quat.create(), orientation),
        snappedOrigin
    );
    return new Grid(
        grid.sizeX,
        grid.sizeY,
        grid.sizeZ,
        grid.resX,
        grid.resY,
        grid.resZ,
        snappedTransform
    );
}

/**
 * Build a patient-space matrix that maps destination points back to
 * source points through an inverse scale around a pivot.
 *
 * For each point p_dst the transform computes:
 *   p_src = pivot + (p_dst - pivot) / scaleFactor
 *
 * Expressed as: T(pivot) * S(1/scale) * T(-pivot)
 *
 * This is used by the resampler which works backwards — for each
 * voxel in the scaled grid it looks up where the value came from
 * in the original grid.
 */
export function buildPatientSrcFromDstScale(
    pivot: ReadonlyVec3,
    scaleFactor: number
): mat4 {
    const invScale = 1.0 / scaleFactor;
    const m = mat4.create();
    mat4.translate(m, m, pivot as vec3);
    mat4.scale(m, m, vec3.fromValues(invScale, invScale, invScale));
    mat4.translate(m, m, vec3.negate(vec3.create(), pivot as vec3));
    return m;
}

function assertSameOrientation(a: Grid, b: Grid): void {
    const oA = a.getOrientation();
    const oB = b.getOrientation();
    for (let i = 0; i < 9; i++) {
        if (Math.abs(oA[i] - oB[i]) > 1e-6) {
            throw new Error('Grid orientations must match');
        }
    }
}

/**
 * Clamp a world-space translation delta so the translated grid
 * does not extend further outside the image than the original.
 *
 * Works in voxel space so that oblique orientations are
 * handled correctly.
 */
export function clampTranslationToImageBounds(
    delta: ReadonlyVec3,
    originalGrid: Grid,
    imageGrid: Grid
): vec3 {
    assertSameOrientation(originalGrid, imageGrid);

    const imageSize = imageGrid.getSize();
    const gridSize = originalGrid.getSize();

    const translatedPos = vec3.add(
        vec3.create(),
        originalGrid.getPosition(),
        delta
    );
    const newVoxel = imageGrid.toVoxelSpace(translatedPos);

    for (let i = 0; i < 3; i++) {
        const upperBound = imageSize[i] - gridSize[i];
        if (upperBound >= 0) {
            // Grid fits within image on this axis — clamp normally
            newVoxel[i] = Math.max(0, Math.min(newVoxel[i], upperBound));
        }
        // When gridSize > imageSize the structure already exceeds
        // the image on this axis — allow free movement.
    }

    const clampedWorld = imageGrid.toWorldSpace(newVoxel);
    return vec3.sub(clampedWorld, clampedWorld, originalGrid.getPosition());
}

/**
 * Analytically compute the maximum uniform scale factor that
 * keeps the scaled grid within the image bounds.
 *
 * Works in voxel space so that oblique orientations are
 * handled correctly.
 *
 * The scaled grid is centred on the original grid's centre.
 * At scale s the half-extent per axis is `halfExt * s`, so
 * the max scale is `min(centre, imageSize - centre) / halfExt`.
 */
export function computeMaxScale(originalGrid: Grid, imageGrid: Grid): number {
    assertSameOrientation(originalGrid, imageGrid);

    const imageSize = imageGrid.getSize();
    const gridSize = originalGrid.getSize();

    const originVoxel = imageGrid.toVoxelSpace(originalGrid.getPosition());
    const centre = vec3.fromValues(
        originVoxel[0] + gridSize[0] * 0.5,
        originVoxel[1] + gridSize[1] * 0.5,
        originVoxel[2] + gridSize[2] * 0.5
    );
    const halfExt = vec3.fromValues(
        gridSize[0] * 0.5,
        gridSize[1] * 0.5,
        gridSize[2] * 0.5
    );

    let maxScale = Infinity;
    for (let i = 0; i < 3; i++) {
        if (halfExt[i] < 1e-6) {
            continue;
        }
        maxScale = Math.min(
            maxScale,
            centre[i] / halfExt[i],
            (imageSize[i] - centre[i]) / halfExt[i]
        );
    }

    return Math.max(maxScale, 1.0);
}

/**
 * Flatten per-slice Uint8Array[] into a single contiguous Uint8Array
 * in z-major order (matching the layout expected by resampleGridCPU).
 */
export function flattenSlices(slices: Uint8Array[], grid: Grid): Uint8Array {
    const sliceLen = grid.sizeX * grid.sizeY;
    const flat = new Uint8Array(sliceLen * grid.sizeZ);
    for (let z = 0; z < grid.sizeZ; z++) {
        flat.set(slices[z], z * sliceLen);
    }
    return flat;
}

/**
 * Split a flat Uint8Array back into per-slice Uint8Array[] arrays
 * (the format expected by ContourTexture.resetSdfAndGrid).
 */
export function unflattenToSlices(
    flat: Uint8Array,
    sizeX: number,
    sizeY: number,
    sizeZ: number
): Uint8Array[] {
    const sliceLen = sizeX * sizeY;
    const slices: Uint8Array[] = [];
    for (let z = 0; z < sizeZ; z++) {
        slices.push(flat.slice(z * sliceLen, (z + 1) * sliceLen));
    }
    return slices;
}

/**
 * Build a patient-space matrix that maps destination points back to
 * source points through a per-axis inverse scale around a pivot.
 *
 * For each point p_dst the transform computes:
 *   p_src = pivot + (p_dst - pivot) / scaleVec
 *
 * Expressed as: T(pivot) * S(1/scaleVec) * T(-pivot)
 *
 * Generalises {@link buildPatientSrcFromDstScale} to non-uniform
 * per-axis scaling (e.g. `(1, 2, 1)` for 2x Y-axis scale).
 */
export function buildPatientSrcFromDstAxisScale(
    pivot: ReadonlyVec3,
    scaleVec: ReadonlyVec3
): mat4 {
    const invScale = vec3.fromValues(
        1.0 / scaleVec[0],
        1.0 / scaleVec[1],
        1.0 / scaleVec[2]
    );
    const m = mat4.create();
    mat4.translate(m, m, pivot as vec3);
    mat4.scale(m, m, invScale);
    mat4.translate(m, m, vec3.negate(vec3.create(), pivot as vec3));
    return m;
}

/**
 * Analytically compute the maximum scale factor along a single
 * local grid axis that keeps the scaled grid within image bounds.
 *
 * @param originalGrid  The structure grid before scaling
 * @param imageGrid     The reference image grid
 * @param axisIndex     The local grid axis index (0=X, 1=Y, 2=Z)
 */
export function computeMaxAxisScale(
    originalGrid: Grid,
    imageGrid: Grid,
    axisIndex: number
): number {
    assertSameOrientation(originalGrid, imageGrid);

    const imageSize = imageGrid.getSize();
    const gridSize = originalGrid.getSize();

    const originVoxel = imageGrid.toVoxelSpace(originalGrid.getPosition());
    const centre = originVoxel[axisIndex] + gridSize[axisIndex] * 0.5;
    const halfExt = gridSize[axisIndex] * 0.5;

    if (halfExt < 1e-6) {
        return Infinity;
    }

    const maxScale = Math.min(
        centre / halfExt,
        (imageSize[axisIndex] - centre) / halfExt
    );

    return Math.max(maxScale, 1.0);
}

/**
 * Build a patient-space matrix that maps destination points back
 * to source points through an inverse rotation around a pivot.
 *
 * For each point p_dst the transform computes:
 *   p_src = pivot + R(-angle) * (p_dst - pivot)
 *
 * Expressed as: T(pivot) * R(axis, -angle) * T(-pivot)
 *
 * The angle is negated because the resampler maps dst→src
 * (it undoes the rotation to find the source location).
 *
 * @param pivot  Centre of rotation in patient coordinates
 * @param axis   Rotation axis (unit vector in patient space)
 * @param angle  Rotation angle in radians (positive = CCW
 *               when looking down the axis)
 */
export function buildPatientSrcFromDstRotation(
    pivot: ReadonlyVec3,
    axis: ReadonlyVec3,
    angle: number
): mat4 {
    const q = quat.create();
    quat.setAxisAngle(q, axis as vec3, -angle);
    const rotMat = mat4.fromQuat(mat4.create(), q);

    const m = mat4.create();
    mat4.translate(m, m, pivot as vec3);
    mat4.multiply(m, m, rotMat);
    mat4.translate(m, m, vec3.negate(vec3.create(), pivot as vec3));
    return m;
}

/**
 * Build a forward rotation matrix around a pivot point.
 *
 * Computes: T(pivot) * R(axis, angle) * T(-pivot)
 *
 * Used during drag to rotate the grid for visual preview.
 */
export function buildRotationAroundPivot(
    pivot: ReadonlyVec3,
    axis: ReadonlyVec3,
    angle: number
): mat4 {
    const q = quat.create();
    quat.setAxisAngle(q, axis as vec3, angle);
    const rotMat = mat4.fromQuat(mat4.create(), q);

    const m = mat4.create();
    mat4.translate(m, m, pivot as vec3);
    mat4.multiply(m, m, rotMat);
    mat4.translate(m, m, vec3.negate(vec3.create(), pivot as vec3));
    return m;
}

/**
 * Build an axis-aligned destination grid that encompasses the
 * rotated structure, using the image grid's orientation and
 * resolution.
 *
 * After rotation the structure grid has a different orientation
 * than the image grid. This function:
 * 1. Transforms all 8 corners of the original grid by the
 *    rotation matrix.
 * 2. Projects them into the image grid's voxel space to find
 *    the axis-aligned bounding box.
 * 3. Constructs a new Grid with the image grid's orientation
 *    and resolution, sized to cover the AABB.
 * 4. Snaps the origin to the image lattice.
 *
 * @param originalGrid  The structure grid before rotation
 * @param rotationMat   Forward rotation matrix (pivot-centred)
 * @param imageGrid     Reference image grid for orientation and
 *                      resolution
 */
export function buildAxisAlignedDestGrid(
    originalGrid: Grid,
    rotationMat: ReadonlyMat4,
    imageGrid: Grid
): Grid {
    const res = imageGrid.getRes();
    const imageOrientation = imageGrid.getOrientation();

    // Compute the 8 corners of the original grid in world space,
    // then rotate them
    const srcSize = originalGrid.getSize();
    const voxelToWorld = originalGrid.voxelToWorld as mat4;

    let minVoxel = vec3.fromValues(Infinity, Infinity, Infinity);
    let maxVoxel = vec3.fromValues(-Infinity, -Infinity, -Infinity);

    // Iterate over the 8 corners of the voxel grid
    for (let iz = 0; iz <= 1; iz++) {
        for (let iy = 0; iy <= 1; iy++) {
            for (let ix = 0; ix <= 1; ix++) {
                const voxelCorner = vec3.fromValues(
                    ix * srcSize[0],
                    iy * srcSize[1],
                    iz * srcSize[2]
                );

                // Voxel → world (original) → world (rotated)
                const worldPt = vec3.transformMat4(
                    vec3.create(),
                    voxelCorner,
                    voxelToWorld
                );
                const rotatedPt = vec3.transformMat4(
                    vec3.create(),
                    worldPt,
                    rotationMat as mat4
                );

                // Project into image voxel space
                const imageVoxel = imageGrid.toVoxelSpace(rotatedPt);
                vec3.min(minVoxel, minVoxel, imageVoxel);
                vec3.max(maxVoxel, maxVoxel, imageVoxel);
            }
        }
    }

    // Add 1-voxel padding to avoid boundary clipping
    minVoxel = vec3.fromValues(
        Math.floor(minVoxel[0]) - 1,
        Math.floor(minVoxel[1]) - 1,
        Math.floor(minVoxel[2]) - 1
    );
    maxVoxel = vec3.fromValues(
        Math.ceil(maxVoxel[0]) + 1,
        Math.ceil(maxVoxel[1]) + 1,
        Math.ceil(maxVoxel[2]) + 1
    );

    const newSizeX = Math.max(1, maxVoxel[0] - minVoxel[0]);
    const newSizeY = Math.max(1, maxVoxel[1] - minVoxel[1]);
    const newSizeZ = Math.max(1, maxVoxel[2] - minVoxel[2]);

    // Convert the min corner back to world space for the origin
    const newOrigin = imageGrid.toWorldSpace(minVoxel);

    const newTransform = mat4.fromRotationTranslation(
        mat4.create(),
        quat.fromMat3(quat.create(), imageOrientation),
        newOrigin
    );

    return new Grid(
        Math.round(newSizeX),
        Math.round(newSizeY),
        Math.round(newSizeZ),
        res[0],
        res[1],
        res[2],
        newTransform
    );
}
